import * as React from "react";
import app from "../../core/application";
import * as Constants from "../../core/constants";
import ExportButton from "../../core/ExportButton/ExportButton";
import Spinner from "../../core/Spinner/Spinner";
import TableBooleanFilter from "../../core/TableBooleanFilter/TableBooleanFilter";
import TableCheckboxListFilter, { ICheckboxValue } from "../../core/TableCheckboxListFilter/TableCheckboxListFilter";
import TableDateRangeFilter from "../../core/TableDateRangeFilter/TableDateRangeFilter";
import TableTextFilter from "../../core/TableTextFilter/TableTextFilter";
import debounce from "../../helpers/debounce";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IListResults from "../../types/IListResults";
import IMessageHeader from "../../types/IMessageHeader";
import IRecipient from "../../types/IRecipient";
import IRecipientSearchRequest from "../../types/IRecipientSearchRequest";
import IResponse from "../../types/IResponse";
import RecipientDetailModal from "./RecipientDetailModal";
import RecipientRow from "./RecipientRow";

interface IRecipientListProps extends ITranslationProps {
    message: IMessageHeader;
    loading: boolean;
    search: (request: IRecipientSearchRequest) => Promise<IResponse<IListResults<IRecipient>> | null>;
}

interface IRecipientListState {
    responseTimeStartDate: Date | null;
    responseTimeEndDate: Date | null;
    recipient: string;
    bounced: boolean;
    machineDetection: string[];
    responseText: string;
    page: number;
    moreRecordsExist: boolean;
    recipients: IRecipient[];
    selectedRecipient: IRecipient | null;
    recipientModalOpen: boolean;
}

class RecipientList extends React.Component<IRecipientListProps, IRecipientListState> {
    private exportForm: HTMLFormElement | null;
    private searchWithDebounce = debounce(() => { this.search(); }, 500);

    constructor(props: IRecipientListProps) {
        super(props);

        this.state = {
            responseTimeStartDate: null,
            responseTimeEndDate: null,
            recipient: "",
            bounced: false,
            machineDetection: [],
            responseText: "",
            page: 1,
            moreRecordsExist: false,
            recipients: [],
            selectedRecipient: null,
            recipientModalOpen: false
        };

        this.updateResponseTime = this.updateResponseTime.bind(this);
        this.updateRecipient = this.updateRecipient.bind(this);
        this.updateBounced = this.updateBounced.bind(this);
        this.updateMachineDetection = this.updateMachineDetection.bind(this);
        this.updateResponseText = this.updateResponseText.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.search = this.search.bind(this);
        this.onRowClick = this.onRowClick.bind(this);
        this.onRecipientModalRequestClose = this.onRecipientModalRequestClose.bind(this);
        this.onExportClick = this.onExportClick.bind(this);
    }

    public componentDidMount() {
        this.search(true);
    }

    public render() {
        const application = app();
        const dlToken = application.userSession ? application.userSession.fileDownloadAuthenticationToken : "";

        const machineDetectionValues: ICheckboxValue[] = [
            { key: Constants.MACHINE_DETECTION_FAX, value: Constants.MACHINE_DETECTION_FAX },
            { key: Constants.MACHINE_DETECTION_HUMAN, value: Constants.MACHINE_DETECTION_HUMAN },
            { key: Constants.MACHINE_DETECTION_MACHINE, value: Constants.MACHINE_DETECTION_MACHINE },
            { key: Constants.MACHINE_DETECTION_UNKNOWN, value: Constants.MACHINE_DETECTION_UNKNOWN },
        ];

        const recipientRows: React.ReactNode[] = [];
        if (this.state.recipients) {
            this.state.recipients.forEach((r, idx) => {
                const rowClick = () => { this.onRowClick(r); }
                recipientRows.push(<RecipientRow key={idx} recipient={r} message={this.props.message} onRowClick={rowClick} />)
            });
        }
        const machineDetectionInputs: React.ReactNode[] = [];
        this.state.machineDetection.forEach((machineDetection, index) => {
            machineDetectionInputs.push(<input key={index} type="hidden" name="machineDetection[]" value={machineDetection} />);
        });
        return (
            <div className="card mt-3">

                <div className="card-header">
                    <div style={{ float: "left", marginRight: ".5em" }}>
                        {this.props.t("MessageDetail.Recipients")}
                    </div>
                    {this.props.loading && <Spinner showCircleOnly={true} circleSize={22} style={{ float: "left" }} />}
                </div>
                <div className="card-body recipientlist">
                    <div className="float-right">
                        <ExportButton onClick={this.onExportClick} />
                        <form ref={ref => this.exportForm = ref} method="POST" style={{ "display": "none" }}
                            action={`${application.webServiceBaseURL}/api/message/exportrecipients?dltoken=${dlToken}`}
                            target="_blank">
                            <input type="hidden" name="Recipient" value={this.state.recipient} />
                            <input type="hidden" name="Response" value={this.state.responseText} />
                            <input type="hidden" name="MessageType" value={this.props.message.messageType} />
                            <input type="hidden" name="MessageID" value={this.props.message.id} />
                            <input type="hidden" name="Bounced" value={this.state.bounced ? "true" : "false"} />>
                            <input type="hidden" name="ResponseStartDate" value={this.state.responseTimeStartDate ? this.state.responseTimeStartDate.toJSON() : ""} />
                            <input type="hidden" name="ResponseEndDate" value={this.state.responseTimeEndDate ? this.state.responseTimeEndDate.toJSON() : ""} />
                            {machineDetectionInputs}
                        </form>
                    </div>
                    <table className="table table-sm recipientlist-table">
                        <thead className="thead-light">
                            <tr>
                                {this.props.message.messageType !== Constants.MESSAGE_TYPE_PUSH &&
                                    <th>
                                        <div>{this.props.t("MessageDetail.Recipient")}</div>
                                        <TableTextFilter searchButtonClick={this.search} value={this.state.recipient}
                                            onChange={this.updateRecipient} />
                                    </th>
                                }
                                <th>
                                    <div>{this.props.t("MessageDetail.Response")}</div>
                                    <TableTextFilter searchButtonClick={this.search} value={this.state.responseText}
                                        onChange={this.updateResponseText} />
                                </th>

                                {this.props.message.messageType !== Constants.MESSAGE_TYPE_VOICE &&
                                    <th>
                                        <div>{this.props.t("MessageDetail.ResponseTime")}</div>
                                        <TableDateRangeFilter small={true} startDate={this.state.responseTimeStartDate}
                                            endDate={this.state.responseTimeEndDate} onChange={this.updateResponseTime} />
                                    </th>
                                }

                                {this.props.message.messageType === Constants.MESSAGE_TYPE_EMAIL &&
                                    <th>
                                        <div>{this.props.t("MessageDetail.Bounced")}</div>
                                        <TableBooleanFilter trueLabel={this.props.t("Global.Yes")} value={this.state.bounced}
                                            falseLabel={this.props.t("Global.No")} onChange={this.updateBounced} />
                                    </th>
                                }

                                {this.props.message.messageType === Constants.MESSAGE_TYPE_VOICE &&
                                    <th>
                                        <div>{this.props.t("MessageDetail.MachineDetection")}</div>
                                        <TableCheckboxListFilter values={machineDetectionValues} selectedValues={this.state.machineDetection} onChange={this.updateMachineDetection} />
                                    </th>
                                }
                            </tr>
                        </thead>
                    </table>

                    <table className="table table-sm recipientlist-table table-hover">
                        <thead>
                            <tr>
                                {this.props.message.messageType !== Constants.MESSAGE_TYPE_PUSH &&
                                    <th>{this.props.t("MessageDetail.Recipient")}</th>
                                }
                                <th>{this.props.t("MessageDetail.SentTime")}</th>
                                <th>{this.props.t("MessageDetail.Response")}</th>

                                {this.props.message.messageType !== Constants.MESSAGE_TYPE_VOICE &&
                                    <th>{this.props.t("MessageDetail.ResponseTime")}</th>
                                }

                                {this.props.message.messageType === Constants.MESSAGE_TYPE_SMS &&
                                    <th>
                                        {this.props.t("MessageDetail.DeliveryReceiptTime")}
                                    </th>
                                }

                                {this.props.message.messageType === Constants.MESSAGE_TYPE_EMAIL &&
                                    <th>
                                        {this.props.t("MessageDetail.Bounced")}
                                    </th>
                                }
                                {this.props.message.messageType === Constants.MESSAGE_TYPE_VOICE &&
                                    <th>
                                        {this.props.t("MessageDetail.MachineDetection")}
                                    </th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {recipientRows}
                        </tbody>
                    </table>

                    {this.state.moreRecordsExist &&
                        <div className="mt-2">
                            <button data-id="showMoreButton" className="btn btn-outline-secondary mr-2" style={{ float: "left" }}
                                onClick={this.loadNextPage}>{this.props.t("Global.ShowMore")}
                            </button>
                            {this.props.loading && <Spinner showCircleOnly={true} circleSize={35} style={{ float: "left" }} />}
                        </div>
                    }

                </div>

                <RecipientDetailModal isOpen={this.state.recipientModalOpen}
                    onRequestClose={this.onRecipientModalRequestClose}
                    recipient={this.state.selectedRecipient}
                    messageType={this.props.message.messageType} />

            </div>
        );
    }

    private updateResponseTime(startDate: Date | null, endDate: Date | null) {
        this.setState({ responseTimeStartDate: startDate, responseTimeEndDate: endDate }, this.searchWithDebounce);
    }

    private updateRecipient(value: string) {
        this.setState({ recipient: value }, this.searchWithDebounce);
    }

    private updateBounced(value: boolean) {
        this.setState({ bounced: value }, this.search);
    }

    private updateMachineDetection(selectedValues: string[]) {
        this.setState({ machineDetection: selectedValues }, this.searchWithDebounce);
    }

    private updateResponseText(value: string) {
        this.setState({ responseText: value }, this.searchWithDebounce)
    }

    private search(newSearch: boolean = true) {
        this.setState({ page: newSearch ? 1 : this.state.page }, async () => {
            const response = await this.props.search(this.getSearchRequest());

            if (response && response.success) {
                if (newSearch) {
                    this.setState({ recipients: response.payload.records, moreRecordsExist: response.payload.moreRecordsExist });
                } else {
                    this.setState((state: IRecipientListState) => {
                        state.recipients.push(...response.payload.records);
                        state.moreRecordsExist = response.payload.moreRecordsExist;
                        return state;
                    });
                }
            }
        });
    }

    private loadNextPage() {
        if (!this.props.loading) {
            this.setState({ page: this.state.page + 1 }, () => { this.search(false) });
        }
    }

    private getSearchRequest(): IRecipientSearchRequest {
        return {
            bounced: this.state.bounced,
            machineDetection: this.state.machineDetection,
            messageType: this.props.message.messageType,
            messageID: this.props.message.id,
            page: this.state.page,
            recipient: this.state.recipient,
            response: this.state.responseText,
            responseStartDate: this.state.responseTimeStartDate,
            responseEndDate: this.state.responseTimeEndDate
        };
    }

    private onRowClick(recipient: IRecipient) {
        this.setState({ recipientModalOpen: true, selectedRecipient: recipient });
    }

    private onRecipientModalRequestClose() {
        this.setState({ recipientModalOpen: false, selectedRecipient: null });
    }

    private onExportClick() {
        if (this.exportForm) {
            this.exportForm.submit();
        }
    }
}

export default translateComponent(RecipientList);



// WEBPACK FOOTER //
// ./src/components/MessageDetail/RecipientList.tsx